import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import { useThemeContext } from '@/context/ThemeContext';
import Card from '@/components/common/Card';
import Button from '@/components/common/Button';
import Select from '@/components/common/Select';
import logger from '@/services/logger';

interface SettingsWindowProps {
  windowId: string;
}

/**
 * Settings window component
 * Allows configuring application settings
 */
const SettingsWindow: React.FC<SettingsWindowProps> = ({ windowId }) => {
  const dispatch = useAppDispatch();
  const { 
    mode, 
    setMode, 
    colorScheme, 
    setColorScheme, 
    isDarkMode 
  } = useThemeContext();
  
  const [activeTab, setActiveTab] = useState<'appearance' | 'analysis' | 'system'>('appearance');

  const handleSaveSettings = () => {
    logger.info('Settings saved', { 
      module: 'settings', 
      data: { windowId } 
    });
  };

  const renderAppearanceSettings = () => (
    <div className="space-y-4">
      <div>
        <h4 className="font-medium mb-2">Theme</h4>
        <div className="grid grid-cols-1 gap-2">
          <Select
            label="Color Mode"
            value={mode}
            onChange={(e) => setMode(e.target.value as 'light' | 'dark')}
            options={[
              { value: 'light', label: 'Light' },
              { value: 'dark', label: 'Dark' },
            ]}
          />
          
          <Select
            label="Color Scheme"
            value={colorScheme}
            onChange={(e) => setColorScheme(e.target.value as 'default' | 'blue' | 'green' | 'purple')}
            options={[
              { value: 'default', label: 'Default' },
              { value: 'blue', label: 'Blue' },
              { value: 'green', label: 'Green' },
              { value: 'purple', label: 'Purple' },
            ]}
          />
        </div>
      </div>
      
      <div>
        <h4 className="font-medium mb-2">UI Preferences</h4>
        <div className="grid grid-cols-1 gap-2">
          <Select
            label="Font Size"
            value="medium"
            onChange={() => {}}
            options={[
              { value: 'small', label: 'Small' },
              { value: 'medium', label: 'Medium' },
              { value: 'large', label: 'Large' },
            ]}
          />
        </div>
      </div>
    </div>
  );

  const renderAnalysisSettings = () => (
    <div className="space-y-4">
      <div>
        <h4 className="font-medium mb-2">Analysis Configuration</h4>
        <div className="grid grid-cols-1 gap-2">
          <Select
            label="Default Analysis Type"
            value="standard"
            onChange={() => {}}
            options={[
              { value: 'quick', label: 'Quick Analysis' },
              { value: 'standard', label: 'Standard Analysis' },
              { value: 'detailed', label: 'Detailed Analysis' },
            ]}
          />
          
          <Select
            label="LLM Provider"
            value="openai"
            onChange={() => {}}
            options={[
              { value: 'openai', label: 'OpenAI' },
              { value: 'anthropic', label: 'Anthropic' },
              { value: 'google', label: 'Google AI' },
            ]}
          />
        </div>
      </div>
      
      <div>
        <h4 className="font-medium mb-2">API Configuration</h4>
        <div className="grid grid-cols-1 gap-2">
          <div>
            <label className="block text-sm font-medium mb-1">OpenAI API Key</label>
            <input 
              type="password" 
              className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
              placeholder="Enter API key"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderSystemSettings = () => (
    <div className="space-y-4">
      <div>
        <h4 className="font-medium mb-2">Logging</h4>
        <div className="grid grid-cols-1 gap-2">
          <Select
            label="Log Level"
            value="info"
            onChange={() => {}}
            options={[
              { value: 'debug', label: 'Debug' },
              { value: 'info', label: 'Info' },
              { value: 'warn', label: 'Warning' },
              { value: 'error', label: 'Error' },
            ]}
          />
        </div>
      </div>
      
      <div>
        <h4 className="font-medium mb-2">Data Storage</h4>
        <div className="flex items-center space-x-2">
          <input 
            type="checkbox" 
            id="autosave" 
            className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
          />
          <label htmlFor="autosave" className="text-sm">Enable Auto-Save</label>
        </div>
        
        <div className="mt-2">
          <Button 
            variant="danger" 
            size="small"
          >
            Clear Local Storage
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        <button
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'appearance' 
              ? 'border-b-2 border-primary text-primary' 
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
          onClick={() => setActiveTab('appearance')}
        >
          Appearance
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'analysis' 
              ? 'border-b-2 border-primary text-primary' 
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
          onClick={() => setActiveTab('analysis')}
        >
          Analysis
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'system' 
              ? 'border-b-2 border-primary text-primary' 
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
          onClick={() => setActiveTab('system')}
        >
          System
        </button>
      </div>
      
      <div className="flex-grow overflow-auto p-4">
        {activeTab === 'appearance' && renderAppearanceSettings()}
        {activeTab === 'analysis' && renderAnalysisSettings()}
        {activeTab === 'system' && renderSystemSettings()}
      </div>
      
      <div className="p-2 border-t border-gray-200 dark:border-gray-700 flex justify-end">
        <Button 
          variant="primary" 
          size="small"
          onClick={handleSaveSettings}
        >
          Save Settings
        </Button>
      </div>
    </div>
  );
};

export default SettingsWindow; 